import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import FileInput from "../file/FileInputComponent";
import { LoadingButton } from "@mui/lab";
import { requestApi } from "../../../helpers/RequestApi";
import { POST } from "../../../utils/MethodUtils";
import { INVOICE_URL } from "../../../utils/UrlsUtils";
import { toastr } from "react-redux-toastr";
import getErrorApi from "../../../helpers/GetErrorApi";
import { useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";
import { ADMIN_INVOICE_PAGE } from "../../../utils/RouteUtils";
import { useNavigate } from "react-router-dom";
import MenuItem from "@mui/material/MenuItem";
import { Select } from "@mui/material";
import { InputInterface } from "../../../interfaces/InputInterface";
import { SelectChangeEvent } from "@mui/material/Select";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

interface FormState {
  model: InputInterface;
  invoiceTemplate: InputInterface;
  multiple: InputInterface;
  bonLivraison: InputInterface;
}

const models = ["gpt-5-mini", "gpt-5"];

const UploadInvoiceComponent: React.FC = React.memo(() => {
  const { t } = useTranslation();
  const [hasFile, setHasFile] = React.useState<boolean>(false);
  const fileRef: any = useRef();
  const [loading, setLoading] = React.useState(false);
  const token = useAppSelector((state: RootState) => state.globalState.token);
  const navigate = useNavigate();
  const invoiceTemplates = useAppSelector(
    (state: RootState) => state.globalState.invoiceTemplates
  );
  const getDefaultValue = React.useCallback((): FormState => {
    return {
      model: {
        value: models[0],
        error: "",
      },
      invoiceTemplate: {
        value:
          invoiceTemplates?.find((x) => x.userIdentifier === null)?.id ?? 0,
        error: "",
      },
      multiple: {
        value: false,
        error: "",
      },
      bonLivraison: {
        value: false,
        error: "",
      },
    };
  }, [invoiceTemplates]);
  const [values, setValues] = React.useState<FormState>(getDefaultValue());

  const saveInvoice = React.useCallback(async () => {
    setLoading(true);
    const invoiceBody = new FormData();
    invoiceBody.append("file", fileRef.current.getValue());
    invoiceBody.append("model", values.model.value);
    invoiceBody.append("multiple", values.multiple.value);
    invoiceBody.append("bonLivraison", values.bonLivraison.value);
    invoiceBody.append(
      "userIdentifier",
      invoiceTemplates?.find((x) => x.id === values.invoiceTemplate.value)
        ?.userIdentifier ?? "null"
    );
    const response = await requestApi({
      method: POST,
      path: INVOICE_URL,
      allowError: true,
      timeout: 20 * 60_000,
      token: token,
      body: invoiceBody,
      formData: true,
    });
    if (response.statusCode === 201) {
      toastr.success(t("word.success"), t("word.success"));
      navigate(ADMIN_INVOICE_PAGE + "/" + response.content.id);
    } else {
      for (let message of getErrorApi(response.content)) {
        toastr.error(t("word.error"), t(message));
      }
    }
    setLoading(false);
  }, [
    invoiceTemplates,
    navigate,
    t,
    token,
    values.bonLivraison.value,
    values.invoiceTemplate.value,
    values.model.value,
    values.multiple.value,
  ]);

  const handleChangeSelect = React.useCallback(
    (prop: keyof FormState) => (event: SelectChangeEvent) => {
      setValues((v) => {
        return {
          ...v,
          [prop]: {
            ...v[prop],
            value: event.target.value as string,
            error: "",
          },
        };
      });
    },
    []
  );
  const fournisseurs = useAppSelector(
    (state: RootState) => state.globalState.fournisseurs
  );
  const suppliers = useAppSelector(
    (state: RootState) => state.globalState.suppliers
  );
  const getSuppliers = React.useCallback(() => {
    return (
      fournisseurs?.filter((f) => suppliers?.includes(f.userIdentifier)) ?? []
    ).sort((a, b) => a.userIdentifier.localeCompare(b.userIdentifier));
  }, [fournisseurs, suppliers]);
  const [thisSuppliers, setThisSuppliers] = React.useState(getSuppliers());
  const handleChange = React.useCallback(
    (prop: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setValues((v) => {
        return {
          ...v,
          [prop]: { ...v[prop], value: event.target.checked, error: "" },
        };
      });
    },
    []
  );

  React.useEffect(() => {
    setThisSuppliers(getSuppliers());
  }, [fournisseurs, suppliers]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedInvoiceTemplate = invoiceTemplates?.find(
    (x) =>
      x.userIdentifier === values.invoiceTemplate.value ||
      (values.invoiceTemplate.value === "" && !x.userIdentifier)
  );
  return (
    <>
      {hasFile && values.invoiceTemplate.value && (
        <pre>
          {
            invoiceTemplates?.find((x) => x.id === values.invoiceTemplate.value)
              ?.systemPrompt
          }
        </pre>
      )}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <FileInput
          ref={fileRef}
          accept="application/pdf"
          label={t("sentence.selectPdf")}
          setHasFile={setHasFile}
        />
        {hasFile && (
          <>
            <Select
              value={values.model.value}
              label={t("word.model")}
              onChange={handleChangeSelect("model")}
              sx={{ marginLeft: 1 }}
            >
              {models.map((model) => (
                <MenuItem value={model} key={model}>
                  {model}
                </MenuItem>
              ))}
            </Select>
            <Select
              value={values.invoiceTemplate.value}
              label={t("word.invoiceTemplate")}
              onChange={handleChangeSelect("invoiceTemplate")}
              sx={{ marginLeft: 2 }}
              renderValue={(selected) => {
                const invoiceTemplate = invoiceTemplates?.find(
                  (x) => x.id?.toString() === selected.toString()
                );
                if (!invoiceTemplate?.userIdentifier) {
                  return t("sentence.byDefault");
                }
                const supplier = thisSuppliers.find(
                  (x) => x.userIdentifier === invoiceTemplate.userIdentifier
                );
                return `[${supplier?.userIdentifier}] ${supplier?.ctIntitule}`;
              }}
            >
              {invoiceTemplates?.map((invoiceTemplate, index) => {
                const supplier = thisSuppliers.find(
                  (x) => x.userIdentifier === invoiceTemplate.userIdentifier
                );
                return (
                  <MenuItem value={invoiceTemplate.id ?? ""} key={index}>
                    {invoiceTemplate.userIdentifier
                      ? `[${supplier?.userIdentifier}] ${supplier?.ctIntitule}`
                      : t("sentence.byDefault")}
                  </MenuItem>
                );
              })}
            </Select>
            <FormControlLabel
              sx={{ marginLeft: 2 }}
              control={
                <Checkbox
                  checked={values.multiple.value}
                  onChange={handleChange("multiple")}
                />
              }
              label={t("word.multipleInvoice")}
            />
            <FormControlLabel
              sx={{ marginLeft: 2 }}
              control={
                <Checkbox
                  checked={values.bonLivraison.value}
                  onChange={handleChange("bonLivraison")}
                />
              }
              label={t("word.fdocentete.doType.3")}
            />
            <a
              target="_blank"
              href={"https://platform.openai.com/docs/overview"}
              rel="noreferrer"
              style={{
                marginLeft: 5,
              }}
            >
              {t("word.price")}
            </a>
          </>
        )}
      </Box>
      {hasFile && (
        <>
          {selectedInvoiceTemplate && (
            <pre>{selectedInvoiceTemplate.systemPrompt}</pre>
          )}
          <LoadingButton
            variant="contained"
            loading={loading}
            onClick={saveInvoice}
          >
            {t("word.save")}
          </LoadingButton>
        </>
      )}
    </>
  );
});

export default UploadInvoiceComponent;
